import { useEffect, useMemo, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Calendar } from "@/shared/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { Skeleton, SkeletonStyles } from "@/shared/ui/skeleton";
import { useAppContext } from "@/app/providers/AppContext";
import WavesColumn from "@/domains/waves/components/WavesColumn";
import { todayInTz, hourInTz } from "@/shared/lib/timezone";
import type { ApiFn, Entitlements, Profile } from "@/shared/lib/types";

interface DatesResponse {
  dates: string[];
}

interface OverviewProps {
  profile: Profile | null;
  entitlements: Entitlements | null;
  onUpgrade: () => void;
  api: ApiFn;
  loading?: boolean;
  onNewWatch?: () => void;
}

function getGreeting(now: Date): string {
  const hour = hourInTz(now);
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

function getFirstName(name?: string): string {
  const trimmed = name?.trim();
  if (!trimmed) return "there";
  return trimmed.split(/\s+/)[0];
}

function formatSessionHeaderDate(value: string): string {
  return parseDate(value).toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function parseDate(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function shiftDate(value: string, days: number): string {
  const parsed = parseDate(value);
  parsed.setDate(parsed.getDate() + days);
  return formatDate(parsed);
}

function coerceTradingDay(value: string): string {
  const parsed = parseDate(value);
  if (parsed.getDay() === 6) parsed.setDate(parsed.getDate() - 1);
  if (parsed.getDay() === 0) parsed.setDate(parsed.getDate() - 2);
  return formatDate(parsed);
}

function previousTradingDay(value: string): string {
  const parsed = parseDate(value);
  parsed.setDate(parsed.getDate() - 1);
  while (parsed.getDay() === 0 || parsed.getDay() === 6) {
    parsed.setDate(parsed.getDate() - 1);
  }
  return formatDate(parsed);
}

function resolveDefaultDate(today: string, recorded: string[], minDate: string): string {
  const candidate = coerceTradingDay(today);
  if (recorded.includes(candidate) && candidate >= minDate) return candidate;

  const previous = previousTradingDay(candidate);
  if (recorded.includes(previous) && previous >= minDate) return previous;

  return recorded.find((day) => day <= previous && day >= minDate) ?? previous;
}
function formatPickerLabel(value: string): string {
  const [year, month, day] = value.split("-");
  return `${month}-${day}-${year}`;
}

function OverviewDatePicker({
  value,
  today,
  onChange,
}: {
  value: string;
  today: string;
  onChange: (value: string) => void;
}) {
  const selected = parseDate(value);
  const maxDate = parseDate(today);
  const minDate = parseDate(shiftDate(today, -20));
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-8 gap-2 px-3 font-mono text-xs tracking-normal text-foreground">
          <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
          {formatPickerLabel(value)}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto overflow-hidden bg-white p-0 text-neutral-950 dark:bg-popover dark:text-popover-foreground">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          disabled={[{ before: minDate, after: maxDate }, { dayOfWeek: [0, 6] }]}
          startMonth={minDate}
          endMonth={maxDate}
          onSelect={(selectedDay) => {
            if (!selectedDay) return;
            onChange(formatDate(selectedDay));
            setOpen(false);
          }}
        />
        <div className="flex items-center justify-end border-t border-border px-3 py-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              onChange(coerceTradingDay(today));
              setOpen(false);
            }}
          >
            Today
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
export default function Overview({ profile, api, loading = false }: OverviewProps) {
  const now = new Date();
  const today = todayInTz();
  const greeting = getGreeting(now);
  const showNameSkeleton = loading && !profile;
  const { wavesOverview, setWavesOverview } = useAppContext();
  const recorded = wavesOverview.dates;
  const [date, setDate] = useState<string>(() => coerceTradingDay(today));
  const [userSelectedDate, setUserSelectedDate] = useState(false);
  const minSelectableDate = useMemo(() => shiftDate(today, -20), [today]);
  const defaultDate = useMemo(() => resolveDefaultDate(today, recorded, minSelectableDate), [minSelectableDate, recorded, today]);
  const isLive = date === today;
  const headerDate = formatSessionHeaderDate(date);

  useEffect(() => {
    let cancelled = false;

    const loadDates = async () => {
      try {
        const response = await api<DatesResponse>("GET", "/api/waves/dates", undefined, { silent: true });
        if (!cancelled) {
          const dates = response?.dates ?? [];
          setWavesOverview((current) => ({ ...current, dates }));
        }
      } catch {
        if (!cancelled && recorded.length === 0) {
          setWavesOverview((current) => ({ ...current, dates: [] }));
        }
      }
    };

    void loadDates();
    const timer = window.setInterval(() => {
      void loadDates();
    }, 300000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [api, recorded.length, setWavesOverview]);

  useEffect(() => {
    if (!userSelectedDate) setDate(defaultDate);
  }, [defaultDate, userSelectedDate]);


  return (
    <div className="space-y-5 fade-up">
      {showNameSkeleton ? <SkeletonStyles /> : null}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-muted-foreground">
            {headerDate}
          </div>
          <h1 className="watches-page-title font-bold leading-tight">
            <span className="inline-flex flex-wrap items-center gap-2">
              {greeting},
              {showNameSkeleton ? (
                <Skeleton width={118} height={28} borderRadius={6} />
              ) : (
                getFirstName(profile?.name)
              )}
            </span>
          </h1>
          <p className="mt-1 max-w-4xl text-sm text-muted-foreground">
            SPY and QQQ options flow: cumulative net premium, 0DTE gamma exposure,
            sentiment, sweeps, and near-term expirations up to two weeks out.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <OverviewDatePicker
            value={date}
            today={today}
            onChange={(selectedDate) => {
              setUserSelectedDate(true);
              setDate(selectedDate);
            }}
          />
          {date !== defaultDate ? (
            <button
              type="button"
              onClick={() => {
                setUserSelectedDate(false);
                setDate(defaultDate);
              }}
              className="h-8 rounded-md border bg-background px-3 text-xs hover:bg-accent"
            >
              Latest
            </button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <WavesColumn symbol="SPY" date={date} isLive={isLive} api={api} />
        <WavesColumn symbol="QQQ" date={date} isLive={isLive} api={api} />
      </div>

      <p className="text-[11px] text-muted-foreground">
        Net-premium curves are approximations derived from 5-minute volume deltas
        signed by trade location. GEX, flow, put/call ratios, and sweep counts
        are computed from the live option chain and recent contract trade tape.
      </p>
    </div>
  );
}
